import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "modal-mask"
}
const _hoisted_2 = { class: "modal-wrapper" }
const _hoisted_3 = { class: "modal-dialog modal-dialog-scrollable" }
const _hoisted_4 = { class: "modal-content" }
const _hoisted_5 = {
  class: "modal-body",
  style: {"height":"400px"}
}
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = { key: 2 }
const _hoisted_9 = { class: "form-group" }
const _hoisted_10 = { class: "input-group" }
const _hoisted_11 = ["value"]
const _hoisted_12 = { class: "input-group-append" }
const _hoisted_13 = { class: "form-group" }
const _hoisted_14 = { class: "input-group" }
const _hoisted_15 = ["value"]
const _hoisted_16 = { class: "input-group-append" }
const _hoisted_17 = { class: "modal-footer" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = ["disabled"]

import { computed, watch, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import { MonthPicker, MonthPickerInput } from 'vue-month-picker'

    import { MonthYearHelpers } from '@common/types'
    import type { SchedulingSettingsReadModel } from '@Quest/CorporateScenarios/readmodels'

    import { useSchedulingSettingsQueryV1 } from '@Quest/CorporateScenarios/queries-vue-composables'
    import { useOptimizeCorporateScenarioScheduleCommandV1 } from '@Quest/CorporateScenarios/commands-vue-composables'

    import type { SchedulerDialogViewModel } from '@Quest/CorporateScenarios/Components/SchedulerDialog'
    import type { MonthPickerDate } from '@common/Components/MonthPickerDialog'


    // ======================
    // Props
    // ======================

    
export default /*@__PURE__*/_defineComponent({
  __name: 'SchedulerDialog',
  props: {
    viewModel: {}
  },
  emits: ["scheduled"],
  setup(__props: any, { emit: __emit }) {

    const props = __props
    const emit = __emit
    const vm = props.viewModel


    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))
    const scenarioId = computed(() => String(route.params.scenarioId ?? ''))

    const opened = computed(() => vm?.opened.value ?? false)


    // ======================
    // Query
    // ======================

    const {
        runSchedulingSettingsQueryV1,
        modelSchedulingSettingsQueryV1
    } = useSchedulingSettingsQueryV1(workspaceId, strategyId, scenarioId)

    const rm = modelSchedulingSettingsQueryV1 as Ref<SchedulingSettingsReadModel | null>


    // ======================
    // Command
    // ======================

    const {
        invokeOptimizeCorporateScenarioScheduleCommandV1,
        succeedOptimizeCorporateScenarioScheduleCommandV1,
        result: schedulingResult
    } = useOptimizeCorporateScenarioScheduleCommandV1(workspaceId, strategyId, scenarioId)


    // ======================
    // Local State
    // ======================

    const isScheduling = ref(false)
    const show1 = ref(false)
    const show2 = ref(false)

    const scenarioStartDate = ref<Date | null>(null)
    const scenarioEndDate = ref<Date | null>(null)

    const minStartDate = ref<MonthPickerDate | null>(null)
    const minStartDateFrom = ref<Date | null>(null)
    const minStartDateTo = ref<Date | null>(null)

    const maxStartDate = ref<MonthPickerDate | null>(null)
    const maxStartDateFrom = ref<Date | null>(null)
    const maxStartDateTo = ref<Date | null>(null)


    // ======================
    // Derived
    // ======================

    const defaultYear1 = computed(() => minStartDate.value?.year ?? 0)
    const defaultMonth1 = computed(() => minStartDate.value?.monthIndex ?? 0)

    const defaultYear2 = computed(() => maxStartDate.value?.year ?? 0)
    const defaultMonth2 = computed(() => maxStartDate.value?.monthIndex ?? 0)


    // ======================
    // Watch opened
    // ======================

    watch(opened, (val) =>
    {
        if (val === true)
        {
            rm.value = null // 🔥 restore Vue 2 behaviour
            runSchedulingSettingsQueryV1()
        } else
        {
            isScheduling.value = false
            schedulingResult.value = null
        }
    })

    // ======================
    // Watch Query Result
    // ======================

    watch(rm, (current) =>
    {
        if (!current) return

        scenarioStartDate.value = new Date(current.ScenarioStartDateStr)
        scenarioEndDate.value = new Date(current.ScenarioEndDateStr)

        const minStart = new Date(current.MinStartDateStr)
        minStartDate.value = {
            year: minStart.getFullYear(),
            monthIndex: minStart.getMonth() + 1
        }

        minStartDateFrom.value = new Date(
            scenarioStartDate.value.getFullYear(),
            scenarioStartDate.value.getMonth(),
            0, 0, 0, 0, 0
        )

        minStartDateTo.value = new Date(
            scenarioEndDate.value.getFullYear(),
            scenarioEndDate.value.getMonth() + 1,
            0
        )

        const maxStart = new Date(current.MaxStartDateStr)
        maxStartDate.value = {
            year: maxStart.getFullYear(),
            monthIndex: maxStart.getMonth() + 1
        }

        maxStartDateFrom.value = new Date(
            scenarioStartDate.value.getFullYear(),
            scenarioStartDate.value.getMonth(),
            0, 0, 0, 0, 0
        )

        maxStartDateTo.value = new Date(
            scenarioEndDate.value.getFullYear(),
            scenarioEndDate.value.getMonth() + 1,
            0
        )
    })


    // ======================
    // Watch Command Success
    // ======================

    watch(succeedOptimizeCorporateScenarioScheduleCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            isScheduling.value = false
            emit('scheduled', schedulingResult.value)
        }
    })


    // ======================
    // Methods
    // ======================

    function onMinStartDateChange(date: MonthPickerDate)
    {
        minStartDate.value = date
    }

    function onMaxStartDateChange(date: MonthPickerDate)
    {
        maxStartDate.value = date
    }

    function handleOptimizeCorporateScenarioScheduleCommand()
    {
        if (!minStartDate.value || !maxStartDate.value) return

        isScheduling.value = true
        schedulingResult.value = null

        const minStartDatePayload = MonthYearHelpers.build(
            minStartDate.value.year!,
            minStartDate.value.monthIndex!
        )

        const maxStartDatePayload = MonthYearHelpers.build(
            maxStartDate.value.year!,
            maxStartDate.value.monthIndex!
        )

        invokeOptimizeCorporateScenarioScheduleCommandV1({
            minStartDate: minStartDatePayload,
            maxStartDate: maxStartDatePayload
        })
    }

    function close()
    {
        vm.close()
    }

return (_ctx: any,_cache: any) => {
  return (opened.value === true)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", { class: "modal-header" }, [
                _cache[4] || (_cache[4] = _createElementVNode("h5", { class: "modal-title" }, " Scheduler ", -1 /* CACHED */)),
                _createElementVNode("button", {
                  onClick: close,
                  type: "button",
                  class: "close"
                }, "×")
              ]),
              _createElementVNode("div", _hoisted_5, [
                (_unref(rm) != null && isScheduling.value === true)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [...(_cache[5] || (_cache[5] = [
                      _createElementVNode("p", { class: "text-center text-primary my-5" }, [
                        _createElementVNode("i", {
                          class: "fa-solid fa-robot fa-beat-fade",
                          style: {"font-size":"120px"}
                        })
                      ], -1 /* CACHED */)
                    ]))]))
                  : _createCommentVNode("v-if", true),
                (_unref(rm) != null && isScheduling.value === false && _unref(schedulingResult) != null)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("p", null, "Analyzed (unique) portfolios: " + _toDisplayString(_unref(schedulingResult).NumberOfUniquePortfolios), 1 /* TEXT */),
                      _createElementVNode("p", null, "Found (valid) portfolios: " + _toDisplayString(_unref(schedulingResult).NumberOfValidPortfolios), 1 /* TEXT */)
                    ]))
                  : _createCommentVNode("v-if", true),
                (_unref(rm) && isScheduling.value === false && _unref(schedulingResult) == null)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      _createElementVNode("div", _hoisted_9, [
                        _cache[7] || (_cache[7] = _createElementVNode("label", null, "Earliest start date:", -1 /* CACHED */)),
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("input", {
                            value: minStartDate.value ? `${minStartDate.value.month}, ${minStartDate.value.year}` : '',
                            type: "text",
                            class: "form-control pointer bg-white",
                            readonly: "",
                            onClick: _cache[0] || (_cache[0] = ($event: any) => (show1.value = !show1.value))
                          }, null, 8 /* PROPS */, _hoisted_11),
                          _createElementVNode("div", _hoisted_12, [
                            _createElementVNode("button", {
                              class: "btn btn-outline-secondary",
                              type: "button",
                              onClick: _cache[1] || (_cache[1] = ($event: any) => (show1.value = !show1.value))
                            }, [...(_cache[6] || (_cache[6] = [
                              _createElementVNode("i", {
                                class: "fa-regular fa-calendar",
                                "aria-hidden": "true"
                              }, null, -1 /* CACHED */)
                            ]))])
                          ])
                        ]),
                        _withDirectives(_createVNode(_unref(MonthPicker), {
                          onChange: onMinStartDateChange,
                          "min-date": minStartDateFrom.value,
                          "max-date": minStartDateTo.value,
                          "default-month": defaultMonth1.value,
                          "default-year": defaultYear1.value
                        }, null, 8 /* PROPS */, ["min-date", "max-date", "default-month", "default-year"]), [
                          [_vShow, show1.value === true]
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_13, [
                        _cache[9] || (_cache[9] = _createElementVNode("label", null, "Latest start date:", -1 /* CACHED */)),
                        _createElementVNode("div", _hoisted_14, [
                          _createElementVNode("input", {
                            value: maxStartDate.value ? `${maxStartDate.value.month}, ${maxStartDate.value.year}` : '',
                            type: "text",
                            class: "form-control pointer bg-white",
                            readonly: "",
                            onClick: _cache[2] || (_cache[2] = ($event: any) => (show2.value = !show2.value))
                          }, null, 8 /* PROPS */, _hoisted_15),
                          _createElementVNode("div", _hoisted_16, [
                            _createElementVNode("button", {
                              class: "btn btn-outline-secondary",
                              type: "button",
                              onClick: _cache[3] || (_cache[3] = ($event: any) => (show2.value = !show2.value))
                            }, [...(_cache[8] || (_cache[8] = [
                              _createElementVNode("i", {
                                class: "fa-regular fa-calendar",
                                "aria-hidden": "true"
                              }, null, -1 /* CACHED */)
                            ]))])
                          ])
                        ]),
                        _withDirectives(_createVNode(_unref(MonthPicker), {
                          onChange: onMaxStartDateChange,
                          "min-date": maxStartDateFrom.value,
                          "max-date": maxStartDateTo.value,
                          "default-month": defaultMonth2.value,
                          "default-year": defaultYear2.value
                        }, null, 8 /* PROPS */, ["min-date", "max-date", "default-month", "default-year"]), [
                          [_vShow, show2.value === true]
                        ])
                      ])
                    ]))
                  : _createCommentVNode("v-if", true)
              ]),
              _createElementVNode("div", _hoisted_17, [
                _createElementVNode("button", {
                  onClick: close,
                  type: "button",
                  class: "btn btn-outline-secondary",
                  disabled: isScheduling.value === true
                }, "Cancel", 8 /* PROPS */, _hoisted_18),
                _createElementVNode("button", {
                  onClick: handleOptimizeCorporateScenarioScheduleCommand,
                  type: "button",
                  class: "btn btn-primary",
                  disabled: isScheduling.value === true
                }, [...(_cache[10] || (_cache[10] = [
                  _createElementVNode("i", { class: "fa-solid fa-robot fa-bounce mr-1" }, null, -1 /* CACHED */),
                  _createTextVNode(" Schedule now ", -1 /* CACHED */)
                ]))], 8 /* PROPS */, _hoisted_19)
              ])
            ])
          ])
        ])
      ]))
    : _createCommentVNode("v-if", true)
}
}

})